/**
 * Video Testimonials Slider
 * Uses Flickity library for carousel functionality
 * Handles:
 * - Video play/pause/reset on slide change
 * - Play button visibility (only on centered slide)
 * - Proper Flickity initialization with resize handling
 */

(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-video-testimonials-slider]";
  const CAROUSEL_SELECTOR = "[data-video-carousel]";
  const SLIDE_SELECTOR = ".video-testimonials-slider__slide";
  const PLAY_BUTTON_SELECTOR = "[data-play-button]";
  const MUTE_TOGGLE_SELECTOR = "[data-mute-toggle]";

  /**
   * Pause and reset a video element
   * IMPORTANT: Do NOT use video.load() as it causes poster snap + single-frame glitches.
   * Prefer pause() + currentTime = 0 to reset playback without reloading the video source.
   */
  const resetVideo = (video) => {
    if (!video) return;
    try {
      video.pause();
      video.currentTime = 0;
      // Do NOT call video.load() - it causes single-frame flash glitches
    } catch (err) {
      // Safari may throw on currentTime = 0 if video not ready
      console.warn("Video reset warning:", err);
    }
  };

  /**
   * Show play button overlay (show play icon, hide pause icon)
   */
  const showPlayButton = (playButton) => {
    if (playButton) {
      const videoWrapper = playButton.closest(".video-testimonials-slider__video-wrapper");
      if (videoWrapper) {
        videoWrapper.removeAttribute("data-playing");
      }
      playButton.classList.remove("is-hidden");
      playButton.setAttribute("aria-label", "Play video");
    }
  };

  /**
   * Show pause button overlay (hide play icon, show pause icon)
   */
  const showPauseButton = (playButton) => {
    if (playButton) {
      const videoWrapper = playButton.closest(".video-testimonials-slider__video-wrapper");
      if (videoWrapper) {
        videoWrapper.setAttribute("data-playing", "true");
      }
      playButton.classList.remove("is-hidden");
      playButton.setAttribute("aria-label", "Pause video");
    }
  };

  /**
   * Setup play button handlers
   * Blocks Flickity drag events on play button to prevent interference with video playback.
   * Only plays video if the slide is currently selected/centered.
   */
  const setupPlayButtons = (playButtons, flickityInstance = null) => {
    playButtons.forEach((button) => {
      if (!button) return;

      // Find video element using data-video-id attribute
      const videoId = button.dataset.videoId;
      if (!videoId) {
        console.warn("Play button missing data-video-id attribute");
        return;
      }

      const video = document.getElementById(videoId);
      if (!video) {
        console.warn(`Video element not found for ID: ${videoId}`);
        return;
      }

      // Ensure iOS compatibility attributes are set
      // Videos start muted by default; user can unmute via mute toggle
      video.setAttribute("playsinline", "");
      video.setAttribute("webkit-playsinline", "");
      video.muted = true;

      // Block Flickity drag/click interference from the overlay button
      // Prevent Flickity from initiating drag and firing change/settle immediately after click
      const preventFlickityDrag = (e) => {
        e.stopPropagation();
        // Only preventDefault on mouse/pointer events, not touch (to allow click synthesis)
        if (e.type !== "touchstart") {
          e.preventDefault();
        }
      };

      // Add handlers for all pointer/touch events to prevent Flickity drag
      button.addEventListener("pointerdown", preventFlickityDrag);
      button.addEventListener("mousedown", preventFlickityDrag);
      button.addEventListener("touchstart", preventFlickityDrag, { passive: true });

      // Play/pause toggle logic - shared between click and touch handlers
      const togglePlayPause = () => {
        const slide = button.closest(SLIDE_SELECTOR);
        if (!slide) return;

        // For grid layout (no carousel), always allow play/pause
        // For carousel layout, only play if slide is selected/centered
        const isGridLayout = !flickityInstance;
        const isSelected =
          flickityInstance &&
          (flickityInstance.selectedElement === slide ||
            slide.classList.contains("is-selected") ||
            slide.classList.contains("is-active"));

        if (isGridLayout || isSelected) {
          if (video.paused) {
            // Play video only inside click handler (user gesture required)
            video
              .play()
              .then(() => {
                showPauseButton(button);
              })
              .catch((err) => {
                console.warn("Video play failed:", err);
                // Show play button again if play failed
                showPlayButton(button);
              });
          } else {
            video.pause();
            showPlayButton(button);
          }
        }
      };

      // Track touch state to handle tap vs drag
      let touchStartTime = 0;
      let touchStartX = 0;
      let touchStartY = 0;

      button.addEventListener("touchstart", (e) => {
        touchStartTime = Date.now();
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      }, { passive: true });

      // Handle touch end for mobile - fire play/pause if it was a tap (not drag)
      button.addEventListener("touchend", (e) => {
        const touchDuration = Date.now() - touchStartTime;
        const touchEndX = e.changedTouches[0].clientX;
        const touchEndY = e.changedTouches[0].clientY;
        const touchMoveX = Math.abs(touchEndX - touchStartX);
        const touchMoveY = Math.abs(touchEndY - touchStartY);

        // Only trigger if it was a quick tap with minimal movement (not a drag)
        if (touchDuration < 300 && touchMoveX < 10 && touchMoveY < 10) {
          e.preventDefault();
          e.stopPropagation();
          togglePlayPause();
        }
      });

      // Main click handler - only play if slide is selected (for desktop/mouse)
      button.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        togglePlayPause();
      });

      // Show play button when video ends
      video.addEventListener("ended", () => {
        resetVideo(video);
        // For grid layout, always show play button
        // For carousel layout, only show if slide is selected
        const slide = button.closest(SLIDE_SELECTOR);
        const isGridLayout = !flickityInstance;
        const isSelected =
          flickityInstance &&
          slide &&
          (flickityInstance.selectedElement === slide ||
            slide.classList.contains("is-selected"));
        
        if (isGridLayout || isSelected) {
          showPlayButton(button);
        }
      });

      // Show pause button when video starts playing
      video.addEventListener("play", () => {
        // For grid layout, always show pause button
        // For carousel layout, only show if slide is selected
        const slide = button.closest(SLIDE_SELECTOR);
        const isGridLayout = !flickityInstance;
        const isSelected =
          flickityInstance &&
          slide &&
          (flickityInstance.selectedElement === slide ||
            slide.classList.contains("is-selected"));
        
        if (isGridLayout || isSelected) {
          showPauseButton(button);
        }
      });

      // Show play button when video is paused (user-initiated pause)
      video.addEventListener("pause", () => {
        // For grid layout, always show play button
        // For carousel layout, only show if slide is selected
        const slide = button.closest(SLIDE_SELECTOR);
        const isGridLayout = !flickityInstance;
        const isSelected =
          flickityInstance &&
          slide &&
          (flickityInstance.selectedElement === slide ||
            slide.classList.contains("is-selected"));
        
        if (isGridLayout || isSelected) {
          showPlayButton(button);
        }
      });
    });
  };

  /**
   * Initialize Flickity carousel
   * Configured for center-aligned slides with peek effect
   */
  const initFlickityCarousel = (carouselElement, enableInfinite) => {
    if (typeof Flickity === "undefined") {
      console.warn("Flickity library not loaded");
      return null;
    }

    const options = {
      cellSelector: ".video-testimonials-slider__slide",
      cellAlign: "center", // Center alignment for desktop peek
      contain: false, // Allow peek outside viewport
      wrapAround: enableInfinite,
      freeScroll: false,
      prevNextButtons: false,
      pageDots: false,
      draggable: true,
      adaptiveHeight: false,
      selectedAttraction: 0.025,
      friction: 0.28,
      autoPlay: false,
      percentPosition: false, // Important when using pixel margins
    };

    const flickityInstance = new Flickity(carouselElement, options);

    // Removed parallax handler - it was overwriting CSS transforms and causing scaling conflicts
    // Design doesn't require parallax effect

    return flickityInstance;
  };

  /**
   * Get/set user's global unmute preference for this slider (persists across slide changes)
   * When user unmutes any video, all videos stay unmuted when switching slides
   */
  const getUserPrefersUnmuted = (root) => {
    if (!root) return false;
    return root._userPrefersUnmuted === true;
  };
  const setUserPrefersUnmuted = (root, value) => {
    if (root) root._userPrefersUnmuted = value;
  };

  /**
   * Sync mute toggle button UI to match video.muted state (used when switching slides)
   */
  const syncMuteToggleUI = (root, video) => {
    if (!root || !video) return;
    const toggle = root.querySelector(`[data-mute-toggle][data-video-id="${video.id}"]`);
    if (!toggle) return;
    const isMuted = video.muted;
    toggle.setAttribute("aria-pressed", isMuted ? "true" : "false");
    toggle.setAttribute("aria-label", isMuted ? "Ton einschalten" : "Ton ausschalten");
    toggle.classList.toggle("is-muted", isMuted);
    toggle.classList.toggle("is-unmuted", !isMuted);
  };

  /**
   * Auto-play video on centered slide (muted by default for autoplay)
   * Respects user's unmute preference - once unmuted, all videos stay unmuted on slide change
   */
  const autoPlayVideo = (video, playButton, root = null) => {
    if (!video) return;

    const sliderRoot = root || video.closest(ROOT_SELECTOR);
    const userPrefersUnmuted = getUserPrefersUnmuted(sliderRoot);

    // Ensure iOS compatibility attributes are set
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
    // Respect user's unmute preference; otherwise mute for autoplay
    video.muted = !userPrefersUnmuted;
    syncMuteToggleUI(sliderRoot, video);

    video
      .play()
      .then(() => {
        showPauseButton(playButton);
      })
      .catch((err) => {
        console.warn("Auto-play failed:", err);
        showPlayButton(playButton);
      });
  };

  /**
   * Setup mute/unmute toggle buttons
   * Videos start muted; user can enable sound via the toggle
   * Persists unmute preference across slide changes (global: once unmuted, all videos stay unmuted)
   */
  const setupMuteToggle = (root) => {
    const toggles = root.querySelectorAll(MUTE_TOGGLE_SELECTOR);
    toggles.forEach((btn) => {
      const videoId = btn.dataset.videoId;
      if (!videoId) return;

      const video = document.getElementById(videoId);
      if (!video) return;

      const updateMuteState = () => {
        const isMuted = video.muted;
        btn.setAttribute("aria-pressed", isMuted ? "true" : "false");
        btn.setAttribute("aria-label", isMuted ? "Ton einschalten" : "Ton ausschalten");
        btn.classList.toggle("is-muted", isMuted);
        btn.classList.toggle("is-unmuted", !isMuted);
      };

      // Initial state - restore from persisted preference
      if (getUserPrefersUnmuted(root)) {
        video.muted = false;
      }
      updateMuteState();

      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        e.preventDefault();
        video.muted = !video.muted;
        setUserPrefersUnmuted(root, !video.muted);
        updateMuteState();
      });

      // Block Flickity drag on the mute button
      ["pointerdown", "mousedown"].forEach((ev) => {
        btn.addEventListener(ev, (e) => e.stopPropagation());
      });
      btn.addEventListener("touchstart", (e) => e.stopPropagation(), { passive: true });
    });
  };

  /**
   * Setup carousel event listeners for video management
   * Ensures slide-change resets do not "fight" playback - only resets non-selected videos.
   */
  const setupCarouselEvents = (
    flickityInstance,
    slides,
    videos,
    playButtons,
    enableCenterAutoplay = false,
    rootElement = null
  ) => {
    if (!flickityInstance) return;

    // Find caption element if it exists
    const captionElement = rootElement?.querySelector('[data-video-caption]') || null;

    let previousIndex = flickityInstance.selectedIndex || 0;

    // Listen to Flickity's change event
    flickityInstance.on("change", (index) => {
      // Pause and reset ALL non-selected videos (not just previous)
      // This ensures no videos play in background when switching slides
      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        const video = videos[slideIndex];
        const playButton = playButtons[slideIndex];

        if (isActive) {
          // Selected slide: update active state
          slide.classList.add("is-active");
          slide.classList.add("is-selected");

          // Update caption if element exists
          if (captionElement) {
            const captionText = slide.dataset.caption || '';
            if (captionText) {
              captionElement.textContent = captionText;
              captionElement.style.display = 'block';
            } else {
              captionElement.textContent = '';
              captionElement.style.display = 'none';
            }
          }

          // Sync mute state to user preference; auto-play if enabled
          if (video && rootElement) {
            if (enableCenterAutoplay) {
              autoPlayVideo(video, playButton, rootElement);
            } else {
              const userPrefersUnmuted = getUserPrefersUnmuted(rootElement);
              video.muted = !userPrefersUnmuted;
              syncMuteToggleUI(rootElement, video);
            }
          }
        } else {
          // Non-selected slide: pause and reset video, show play button
          if (video) {
            resetVideo(video);
          }
          if (playButton) {
            showPlayButton(playButton);
          }
          slide.classList.remove("is-active");
          slide.classList.remove("is-selected");
        }
      });

      previousIndex = index;
    });

    // Initial state - reset all videos except the first one
    const initialIndex = flickityInstance.selectedIndex || 0;
    slides.forEach((slide, index) => {
      const isActive = index === initialIndex;
      const video = videos[index];
      const playButton = playButtons[index];

      if (isActive) {
        slide.classList.add("is-active");
        slide.classList.add("is-selected");

        // Set initial caption
        if (captionElement) {
          const captionText = slide.dataset.caption || '';
          if (captionText) {
            captionElement.textContent = captionText;
            captionElement.style.display = 'block';
          } else {
            captionElement.textContent = '';
            captionElement.style.display = 'none';
          }
        }

        // Auto-play or sync mute state for initial centered video
        if (video && rootElement) {
          if (enableCenterAutoplay) {
            setTimeout(() => {
              autoPlayVideo(video, playButton, rootElement);
            }, 100);
          } else {
            const userPrefersUnmuted = getUserPrefersUnmuted(rootElement);
            video.muted = !userPrefersUnmuted;
            syncMuteToggleUI(rootElement, video);
          }
        }
      } else {
        // Reset all other videos
        if (video) {
          resetVideo(video);
        }
        if (playButton) {
          showPlayButton(playButton);
        }
        slide.classList.remove("is-active");
        slide.classList.remove("is-selected");
      }
    });
  };

  /**
   * Setup resize handling for Flickity
   * Ensures proper sizing after video metadata loads
   */
  const setupResizeHandling = (flickityInstance, carousel) => {
    if (!flickityInstance) return;

    // Resize after initial render
    requestAnimationFrame(() => {
      flickityInstance.resize();
    });

    // Resize after window load
    if (document.readyState === "loading") {
      window.addEventListener("load", () => {
        flickityInstance.resize();
      });
    } else {
      flickityInstance.resize();
    }

    // Resize when videos load metadata (poster/video dimensions resolve)
    const videos = carousel.querySelectorAll("video");
    videos.forEach((video) => {
      if (video.readyState >= 1) {
        // Already has metadata
        flickityInstance.resize();
      } else {
        video.addEventListener("loadedmetadata", () => {
          flickityInstance.resize();
        });
      }
    });
  };

  /**
   * Initialize a single slider instance
   */
  const initSlider = (root) => {
    if (root.dataset.videoSliderInitialized === "true") {
      return;
    }

    root.dataset.videoSliderInitialized = "true";

    // Check if this is a grid layout (not a carousel)
    const grid = root.querySelector("[data-video-grid]");
    if (grid) {
      // Grid layout - just setup play buttons, no carousel logic
      const slides = Array.from(grid.querySelectorAll(SLIDE_SELECTOR));
      if (!slides.length) {
        console.warn("Grid layout: No slides found");
        return;
      }

      const videos = slides.map((slide) => {
        const video = slide.querySelector(".video-testimonials-slider__video");
        if (!video) {
          console.warn("Grid layout: Video not found in slide", slide);
        }
        return video;
      }).filter(Boolean); // Remove null/undefined videos

      const playButtons = slides.map((slide) => {
        const button = slide.querySelector(PLAY_BUTTON_SELECTOR);
        if (!button) {
          console.warn("Grid layout: Play button not found in slide", slide);
        }
        return button;
      }).filter(Boolean); // Remove null/undefined buttons

      if (playButtons.length === 0) {
        console.warn("Grid layout: No play buttons found");
        return;
      }

      if (videos.length === 0) {
        console.warn("Grid layout: No videos found");
        return;
      }

      // Setup play button handlers only (no carousel, so no flickityInstance)
      setupPlayButtons(playButtons, null);

      // Setup mute/unmute toggle
      setupMuteToggle(root);

      // FOUC Prevention: Show grid after initialization
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          grid.classList.add('is-ready');
        });
      });

      // Auto-play first video in grid layout (if autoplay is enabled)
      const enableAutoplay = root.dataset.enableCenterAutoplay === "true";
      
      if (enableAutoplay) {
        const firstVideo = videos[0];
        const firstPlayButton = playButtons[0];
        if (firstVideo && firstPlayButton) {
          // Slight delay to ensure DOM is ready
          setTimeout(() => {
            autoPlayVideo(firstVideo, firstPlayButton, root);
          }, 100);
        }
      }

      return;
    }

    // Carousel layout
    const carousel = root.querySelector(CAROUSEL_SELECTOR);
    if (!carousel) return;

    // Wait for Flickity to be loaded
    if (typeof Flickity === "undefined") {
      setTimeout(() => initSlider(root), 100);
      return;
    }

    const slides = Array.from(carousel.querySelectorAll(SLIDE_SELECTOR));
    if (!slides.length) return;

    // Get all videos and play buttons
    const videos = slides.map((slide) => {
      return slide.querySelector(".video-testimonials-slider__video");
    });

    const playButtons = slides.map((slide) => {
      return slide.querySelector(PLAY_BUTTON_SELECTOR);
    });

    const enableInfinite = carousel.dataset.enableInfinite === "true";
    const enableCenterAutoplay = carousel.dataset.centerAutoplay === "true";

    // Wait for images/videos to load before initializing Flickity
    // This ensures proper height calculation
    const initFlickityAfterLoad = () => {
      // Initialize Flickity
      const flickityInstance = initFlickityCarousel(carousel, enableInfinite);
      if (!flickityInstance) return;

      // Store Flickity instance for later use
      carousel.flickityInstance = flickityInstance;

      // Setup resize handling
      setupResizeHandling(flickityInstance, carousel);

      // Setup carousel event listeners for video management
      setupCarouselEvents(flickityInstance, slides, videos, playButtons, enableCenterAutoplay, root);

      // Setup play button handlers - pass flickityInstance so it can check selected slide
      setupPlayButtons(playButtons, flickityInstance);

      // Setup mute/unmute toggle
      setupMuteToggle(root);

      // Setup desktop navigation buttons
      const wrapper = carousel.closest('.video-testimonials-slider__carousel-wrapper');
      const prevBtn = wrapper?.querySelector('[data-slider-prev]');
      const nextBtn = wrapper?.querySelector('[data-slider-next]');

      if (prevBtn && nextBtn) {
        prevBtn.addEventListener('click', () => flickityInstance.previous());
        nextBtn.addEventListener('click', () => flickityInstance.next());

        // Update disabled states based on current position
        const updateNavButtons = () => {
          const isFirst = flickityInstance.selectedIndex === 0;
          const isLast = flickityInstance.selectedIndex === flickityInstance.slides.length - 1;

          prevBtn.disabled = isFirst && !enableInfinite;
          nextBtn.disabled = isLast && !enableInfinite;
        };

        flickityInstance.on('change', updateNavButtons);
        updateNavButtons(); // Set initial state
      }

      // FOUC Prevention: Show carousel after Flickity is ready
      flickityInstance.on('ready', () => {
        if (wrapper) {
          wrapper.classList.add('is-ready');
        }
      });

      // Fallback: If ready event doesn't fire, show after a short delay
      // This ensures the slider becomes visible even if ready event is missed
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (wrapper && !wrapper.classList.contains('is-ready')) {
            wrapper.classList.add('is-ready');
          }
        });
      });
    };

    // Check if images/videos are loaded
    const images = carousel.querySelectorAll("img, video");
    let loadedCount = 0;
    const totalMedia = images.length;

    if (totalMedia === 0) {
      // No media to wait for, initialize immediately
      initFlickityAfterLoad();
    } else {
      // Wait for all media to load
      images.forEach((media) => {
        if (media.complete || media.readyState >= 3) {
          // Already loaded
          loadedCount++;
          if (loadedCount === totalMedia) {
            initFlickityAfterLoad();
          }
        } else {
          media.addEventListener("load", () => {
            loadedCount++;
            if (loadedCount === totalMedia) {
              initFlickityAfterLoad();
            }
          });
          media.addEventListener("error", () => {
            // Count errors as "loaded" to not block initialization
            loadedCount++;
            if (loadedCount === totalMedia) {
              initFlickityAfterLoad();
            }
          });
        }
      });

      // Fallback timeout in case some media never loads
      setTimeout(() => {
        if (!carousel.flickityInstance) {
          initFlickityAfterLoad();
        }
      }, 2000);
    }
  };

  /**
   * Initialize all sliders on the page
   */
  const setupAll = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const initMode = root.dataset.initMode || "eager";

      if (initMode === "lazy" && "IntersectionObserver" in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                observer.disconnect();
                initSlider(root);
              }
            });
          },
          { rootMargin: "200px 0px" }
        );

        observer.observe(root);
        return;
      }

      initSlider(root);
    });
  };

  // Wait for Flickity to load, then initialize
  const checkFlickityAndInit = () => {
    if (typeof Flickity !== "undefined") {
      // Flickity is loaded, initialize sliders
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", setupAll, { once: true });
      } else {
        setupAll();
      }
    } else {
      // Wait a bit and check again
      setTimeout(checkFlickityAndInit, 100);
    }
  };

  // Start checking for Flickity
  checkFlickityAndInit();

  // Re-initialize on section load (for theme editor)
  document.addEventListener("shopify:section:load", (event) => {
    const root = event.target.querySelector(ROOT_SELECTOR);
    if (root) {
      // Remove is-ready class to hide slider during re-initialization
      const carouselWrapper = root.querySelector('.video-testimonials-slider__carousel-wrapper');
      const grid = root.querySelector('.video-testimonials-slider__grid');
      if (carouselWrapper) {
        carouselWrapper.classList.remove('is-ready');
      }
      if (grid) {
        grid.classList.remove('is-ready');
      }

      // Destroy existing Flickity instance if it exists
      const carousel = root.querySelector(CAROUSEL_SELECTOR);
      if (carousel && carousel.flickityInstance) {
        carousel.flickityInstance.destroy();
        delete carousel.flickityInstance;
      }
      delete root.dataset.videoSliderInitialized;
      initSlider(root);
    }
  });
})();
